import { ActionCreatorWithoutPayload, createSlice, PayloadAction, Slice } from '@reduxjs/toolkit'
import { authService } from '@afterpaytouch/portal-api/consumer/auth'
import { cashAuthService } from '@afterpaytouch/portal-api/consumer/auth/cash'
import { lookupService } from '@afterpaytouch/portal-api/consumer/lookup'
import { registrationsService } from '@afterpaytouch/portal-api/consumer/registrations'
import { authenticate, Authentication, AuthenticationErrors, FingerPrint, unauthenticate } from '../../../model'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { v4 as uuid } from 'uuid'
import { AuthCheckCodeStatus, CashAuthStatus, PasswordResetTwoFactorChallenge } from '@afterpaytouch/portal-api/consumer/auth/types'
import { authLogout, createFingerprint } from './actions'
import { VerificationMethod } from '@afterpaytouch/registration'
import { accountService } from '@afterpaytouch/portal-api/consumer/account'

const {
  endpoints: { authLogin, authLogoutEndpoint, authCheckCode, authResetCode, checkEmailEligibility },
} = authService

const {
  endpoints: { cashWebLogin },
} = cashAuthService

const {
  endpoints: { finaliseAccountDetails },
} = accountService

const {
  endpoints: { confirmRegistrationMobile },
} = registrationsService

const {
  endpoints: { emailLookup },
} = lookupService

export interface AuthenticationState {
  profilingId: string | null
  fingerprint: FingerPrint | null
  status: Authentication
  error?: AuthenticationErrors
  passwordResetVerifyCode?: string
  passwordResetChallenges?: PasswordResetTwoFactorChallenge[]
  shouldAllowEmail?: boolean
  enabled2faMethods: Record<VerificationMethod, boolean>
}

export const userInitialState: AuthenticationState = {
  status: unauthenticate(),
  profilingId: null,
  fingerprint: null,
  enabled2faMethods: {
    SMS: true,
    EMAIL: false,
  },
}

const authErrors: Partial<Record<ErrorCode, AuthenticationErrors>> = {
  [ErrorCode.AccountLocked]: AuthenticationErrors.AccountLocked,
  [ErrorCode.AccountDisabled]: AuthenticationErrors.AccountDisabled,
  [ErrorCode.AuthenticationFailure]: AuthenticationErrors.IncorrectPassword,
  [ErrorCode.AuthenticationFailureMaxAttempts]: AuthenticationErrors.FailureMaxAttempts,
  [ErrorCode.TooManyOTPRequests]: AuthenticationErrors.TooManyOTPRequests,
  [ErrorCode.PaywatchCheckFailed]: AuthenticationErrors.PaywatchCheckFailed,
  [ErrorCode.AccountClosed]: AuthenticationErrors.AccountClosed,
}

export const authErrorArray = Object.keys(authErrors) as ErrorCode[]

const slice: Slice = createSlice({
  name: 'auth',
  initialState: userInitialState,
  reducers: {
    createProfilingId: {
      reducer(state: AuthenticationState, action: PayloadAction<string>) {
        state.profilingId = action.payload
      },
      prepare() {
        return { payload: uuid() }
      },
    },
    resetAuthState(state: AuthenticationState) {
      state.profilingId = null
      state.fingerprint = null
      state.error = undefined
      state.passwordResetVerifyCode = undefined
      state.passwordResetChallenges = undefined
      state.shouldAllowEmail = undefined
      state.enabled2faMethods = {
        SMS: true,
        EMAIL: false,
      }
    },
    resetAuthError(state: AuthenticationState) {
      state.error = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createFingerprint.fulfilled, (state, action) => {
        state.fingerprint = action.payload
      })
      .addCase(authLogout.fulfilled, (state) => {
        state.status = unauthenticate()
      })
      .addMatcher(confirmRegistrationMobile.matchFulfilled, (state) => {
        state.status.verificationRequired = false
      })
      .addMatcher(finaliseAccountDetails.matchFulfilled, (state) => {
        state.status = authenticate(false, false)
      })

      .addMatcher(authLogin.matchPending, (state) => {
        state.error = undefined
        state.status = unauthenticate()
      })
      .addMatcher(authLogin.matchRejected, (state, action) => {
        state.status = unauthenticate()
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.error = authErrors[action.payload.errorCode] ?? AuthenticationErrors.OtherError
        } else {
          state.error = AuthenticationErrors.OtherError
        }
      })
      .addMatcher(authLogin.matchFulfilled, (state, action) => {
        const isPersistentSession = action.meta.arg.originalArgs['remember-me']
        state.status = authenticate(action.payload.user.requires2fa, isPersistentSession, isPersistentSession)
      })
      .addMatcher(cashWebLogin.matchFulfilled, (state, action) => {
        if (action.payload.status === CashAuthStatus.AUTHORIZED) {
          const isPersistentSession = action.meta.arg.originalArgs.rememberMe
          state.status = authenticate(false, isPersistentSession, isPersistentSession)
        }
      })
      .addMatcher(authLogoutEndpoint.matchFulfilled, (state) => {
        state.status = unauthenticate()
      })
      .addMatcher(authCheckCode.matchFulfilled, (state, action) => {
        state.passwordResetVerifyCode = action.meta.arg.originalArgs.code
        if (action.payload.status === AuthCheckCodeStatus.TWO_FACTOR_REQUIRED) {
          state.passwordResetChallenges = action.payload.challenges
        }
      })
      .addMatcher(emailLookup.matchFulfilled, (state, action) => {
        state.enabled2faMethods.SMS = action.payload.smsVerificationEnabled
        state.status.verificationRequired = action.payload.smsVerificationRequired
      })
      .addMatcher(checkEmailEligibility.matchFulfilled, (state, action) => {
        state.enabled2faMethods.EMAIL = !action.payload.shouldDisableEmailOption
      })
      .addMatcher(authResetCode.matchRejected, (state, action) => {
        const payload = action.payload as { errorCode?: ErrorCode; data?: { errorCode?: ErrorCode } } | undefined
        const code = payload?.errorCode ?? payload?.data?.errorCode
        state.error = authErrors[code as ErrorCode] ?? AuthenticationErrors.OtherError
      })
      .addMatcher(authResetCode.matchFulfilled, (state) => {
        state.error = undefined
      })
  },
})

export const createProfilingId = slice.actions.createProfilingId as ActionCreatorWithoutPayload<string>
export const resetAuthState = slice.actions.resetAuthState as ActionCreatorWithoutPayload<string>
export const resetAuthError = slice.actions.resetAuthError as ActionCreatorWithoutPayload<string>
export const userReducer = slice.reducer
